import React, { createContext, useContext, useState, useEffect } from 'react';
import { SettingsNativeBridgeFactory } from '../bridge/SettingsNativeBridgeFactory';

export interface VersionInfoData {
	version: string;
	release: string;
	copyrightYear: string;
	thirdPartyNoticesUrl?: string;
}

interface VersionInfoContextType {
	versionInfo: VersionInfoData;
	isLoading: boolean;
}

const VersionInfoContext = createContext<VersionInfoContextType | undefined>(undefined);

export const useVersionInfo = (): VersionInfoContextType => {
	const context = useContext(VersionInfoContext);
	if (!context) {
		throw new Error('useVersionInfo must be used within a VersionInfoProvider');
	}
	return context;
};

interface VersionInfoProviderProps {
	versionInfo?: VersionInfoData;
	children: React.ReactNode;
}

export const VersionInfoProvider: React.FC<VersionInfoProviderProps> = ({
	versionInfo: defaultVersionInfo,
	children,
}) => {
	const [versionInfo, setVersionInfo] = useState<VersionInfoData>(
		defaultVersionInfo || {
			version: '',
			release: '',
			copyrightYear: '',
			thirdPartyNoticesUrl: '',
		}
	);
	const [isLoading, setIsLoading] = useState(!defaultVersionInfo);

	useEffect(() => {
		// Only fetch if version info was not provided
		if (defaultVersionInfo) {
			setIsLoading(false);
			return;
		}

		const fetchVersionInfo = () => {
			try {
				const nativeBridge = SettingsNativeBridgeFactory.getBridge();

				// Wait for bridge initialization to get system info
				const cleanup = nativeBridge.onInitialized((initializeParams) => {
					const { systemInfo } = initializeParams;
					setVersionInfo({
						version: systemInfo.version,
						release: systemInfo.release,
						copyrightYear: systemInfo.year,
						thirdPartyNoticesUrl: systemInfo.thirdPartyNoticesUrl,
					});
					setIsLoading(false);
				});

				return cleanup;
			} catch (error) {
				console.error('Failed to fetch system info from bridge:', error);
				// Keep default version info on error
				setIsLoading(false);
			}
		};

		const cleanup = fetchVersionInfo();
		return cleanup;
	}, [defaultVersionInfo]);

	return (
		<VersionInfoContext.Provider value={{ versionInfo, isLoading }}>
			{children}
		</VersionInfoContext.Provider>
	);
};
